import React, { useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Section } from 'components/Section';
import CurrentCampaigns from './currentCampaigns';
import { CategoryNavigation, FilteredCategoryNavigation } from './categoryNavigation';
import { BrandContentTop, BrandContentBottom } from './brandContent';
import { Filter } from 'components/Filter/themes/BGH-RD3/filter';
import {
  getBrandContent,
  getBrandContentDisplayType,
  getContent,
  getNumberOfFaqs,
  hasBrandContent,
  selectWebCatalog,
  showFilter as showFilterSelector
} from 'views/ProductlistPage/selectors';
import { ProductlistPaging } from './productlistPaging';
import { BrandContentDisplayType } from 'models/BrandContentDisplayType';
import { rm, NO_ELEMENT, withCurrentPage } from 'utils';
import { SEOBlock } from './SEOBlock';
import { isMobile as isMobileSelector } from 'components/Shared/responsive';
import { CategoryFaqs } from 'views/ProductPage/faqs';
import { getFaqs } from 'views/ProductPage/faqs/themes/BGH/selector';
import { GtmViewItem, ProductList } from './productList';
import styles from './styles.scss';
import { Title } from './title';
import { ContentArea, Registry } from 'components/Shared/Markdown';
import { registry } from 'views/ContentPage/components/themes/BGH/registry';
import { Divider } from 'views/ProductPage/themes/BGH/divider';
import { styled } from 'styled-components';
import { GtmSelectItem, GtmViewListItem } from 'store/googleTagManager/actions';
import { ProductlistViewProduct } from 'models/ProductlistViewProduct';
import {
  getBrandListName,
  getProductListName,
  mapProductListViewProductToGtm
} from 'store/googleTagManager/events/utils';
import { GtmItemProduct } from 'store/googleTagManager/types';

const useBrandContentScroll = (displayType: BrandContentDisplayType) => {
  const ref = useRef<HTMLDivElement>(NO_ELEMENT);
  useEffect(() => {
    const shouldScroll = displayType === BrandContentDisplayType.ScrollPast;
    let handle: number;
    if (shouldScroll) {
      handle = window.setTimeout(() => {
        ref.current && ref.current.scrollIntoView();
      }, 0);
    }
    return () => {
      handle && window.clearTimeout(handle);
    };
  }, [displayType]);
  return ref;
};

const DividerWithMargins = styled(Divider)({
  marginTop: '2rem',
  marginBottom: '3rem'
});

export const ProductlistPage = () => {
  const isMobile = useSelector(isMobileSelector);
  const brandContentDisplayType = useSelector(withCurrentPage(getBrandContentDisplayType));
  const filterRef = useBrandContentScroll(brandContentDisplayType);
  const totalNumberOfFaqs = useSelector(withCurrentPage(getNumberOfFaqs));
  const anchorRef = useRef(NO_ELEMENT);
  const faqs = useSelector(getFaqs);
  const showFilter = useSelector(withCurrentPage(showFilterSelector));
  const reduxDispatch = useDispatch();
  const content = useSelector(withCurrentPage(getContent));
  const isBrandPage = useSelector(hasBrandContent);
  const webCatalog = useSelector(withCurrentPage(selectWebCatalog));
  const brandContent = useSelector(withCurrentPage(getBrandContent));

  const createGtmProduct = (product: ProductlistViewProduct, index: number): GtmItemProduct => {
    const listName = isBrandPage
      ? getBrandListName(webCatalog, brandContent?.brandName)
      : getProductListName(webCatalog, product.url);
    return mapProductListViewProductToGtm({ product, indexInList: index, listName });
  };

  const gtmOnProductClick = (product: ProductlistViewProduct, index: number, pageNbr: number) => {
    const gtmItemProduct = createGtmProduct(product, index);

    reduxDispatch(GtmSelectItem({ gtmItemProduct, listPageNbr: pageNbr }));
  };

  const gtmOnViewItem = (items: GtmViewItem[], pageNbr: number) => {
    const gtmItemProducts = items.map(item => {
      return createGtmProduct(item.product, item.index);
    });

    reduxDispatch(GtmViewListItem({ gtmItemProducts, listPageNbr: pageNbr }));
  };

  return (
    <>
      <BrandContentTop />
      <Section title={isMobile ? <Title /> : undefined}>
        <CurrentCampaigns />
        <CategoryNavigation />
      </Section>

      <Registry registry={registry}>
        <ContentArea content={content} />
      </Registry>

      <a ref={anchorRef} />
      {showFilter ? <Filter ref={filterRef} /> : <DividerWithMargins />}
      <ProductList gtmOnItemClick={gtmOnProductClick} gtmOnViewItem={gtmOnViewItem} />
      <ProductlistPaging anchorRef={anchorRef} />
      <CategoryFaqs
        className={styles.faqWrapper}
        faqs={faqs}
        totalNumberOfFaqs={totalNumberOfFaqs}
        title={rm('product_faq_header')}
        phoneSize="1"
        tabletSize="2"
      />
      <FilteredCategoryNavigation />
      <Section>
        <SEOBlock />
      </Section>
      <BrandContentBottom />
    </>
  );
};
